import { useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { useFilters } from "@/context/FilterContext";
import { Button } from "@/components/ui/button";
import { Printer, FileText, TrainFront, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import ParetoChart from "@/components/dashboard/ParetoChart";
import TrendChart from "@/components/dashboard/TrendChart";
import DonutChart from "@/components/dashboard/DonutChart";

export default function Reports() {
  const { params } = useFilters();
  const [summary, setSummary] = useState(null);
  const [pareto, setPareto] = useState({ items: [] });
  const [trend, setTrend] = useState({ items: [], granularity: "daily" });
  const [modes, setModes] = useState({ items: [] });
  const [insights, setInsights] = useState([]);
  const [downloading, setDownloading] = useState(false);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("trams_token");
      const qs = new URLSearchParams(params).toString();
      const res = await fetch(`${API_BASE}/reports/pdf?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `TRAMS_RAMS_Report_${params.start}_to_${params.end}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF report downloaded");
    } catch (e) {
      toast.error("Failed to download PDF");
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const [s, p, t, m, i] = await Promise.all([
        api.get("/metrics/summary", { params }),
        api.get("/metrics/pareto", { params }),
        api.get("/metrics/trend", { params }),
        api.get("/metrics/failure_modes", { params }),
        api.get("/metrics/insights", { params }),
      ]);
      setSummary(s.data); setPareto(p.data); setTrend(t.data); setModes(m.data); setInsights(i.data.insights || []);
    })();
  }, [params.start, params.end, params.line, params.subsystem]);

  const c = summary?.current || {};

  return (
    <div className="space-y-6 pb-8" data-testid="reports-page">
      <div className="flex justify-between items-center trams-no-print bg-white rounded-xl p-4 border border-slate-200/60 kpi-shadow">
        <div className="flex items-center gap-3">
          <FileText className="w-6 h-6 text-[#1e40af]" />
          <div>
            <div className="font-bold text-slate-900">RAMS Performance Report</div>
            <div className="text-xs text-slate-500">Download branded PDF for the current filter, or print the page below</div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.print()} data-testid="reports-print">
            <Printer className="w-3.5 h-3.5 mr-1.5" /> Print
          </Button>
          <Button className="bg-[#1e40af] hover:bg-[#1e3a8a]" onClick={downloadPdf} disabled={downloading} data-testid="reports-download-pdf">
            {downloading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
            {downloading ? "Generating…" : "Download PDF"}
          </Button>
        </div>
      </div>

      <div className="bg-white p-10 rounded-xl border border-slate-200/60 kpi-shadow print:shadow-none print:border-0">
        {/* Report Header */}
        <div className="border-b-4 border-[#1e40af] pb-6 mb-8 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#1e40af] flex items-center justify-center text-white">
                <TrainFront className="w-7 h-7" />
              </div>
              <div>
                <div className="text-xl font-black text-[#1e40af] tracking-tight">TRAMS</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">RAMS Dashboard</div>
              </div>
            </div>
            <h1 className="text-3xl font-black text-slate-900 mt-6 tracking-tight">RAMS Operational Performance Report</h1>
            <p className="text-sm text-slate-500 mt-2">Reliability · Availability · Maintainability · Safety</p>
          </div>
          <div className="text-right text-xs text-slate-500 space-y-1">
            <div><span className="uppercase font-bold tracking-wider">Period:</span> {params.start} — {params.end}</div>
            <div><span className="uppercase font-bold tracking-wider">Line:</span> {params.line === "all" ? "All Lines" : params.line}</div>
            <div><span className="uppercase font-bold tracking-wider">Subsystem:</span> {params.subsystem === "all" ? "All Subsystems" : params.subsystem}</div>
            <div className="pt-2 border-t border-slate-200 mt-2"><span className="uppercase font-bold tracking-wider">Generated:</span> {new Date().toLocaleString()}</div>
          </div>
        </div>

        {/* KPIs */}
        <h2 className="text-sm font-black text-[#1e40af] uppercase tracking-widest mb-4 flex items-center">
          <span className="text-[#93c5fd] mr-2 text-lg">1.</span> Executive Summary
        </h2>
        <div className="grid grid-cols-3 gap-4 mb-10">
          {[
            ["Total Failure Events", c.total_failure_events, "#f59e0b"],
            ["Total Delay (Minutes)", c.total_delay_minutes, "#f59e0b"],
            ["Cancelled Trips", c.cancelled_trips, "#ef4444"],
            ["MTBF (Hours)", c.mtbf_hours, "#22c55e"],
            ["MTTR (Minutes)", c.mttr_minutes, "#3b82f6"],
            ["Availability (%)", c.availability_percent, "#22c55e"],
          ].map(([label, v, color]) => (
            <div key={label} className="border-l-4 pl-4 py-2" style={{ borderColor: color }}>
              <div className="text-[10px] uppercase font-bold text-slate-500 tracking-widest">{label}</div>
              <div className="text-2xl font-black text-slate-900 mt-1">{v?.toLocaleString?.(undefined, { maximumFractionDigits: 2 }) || "—"}</div>
            </div>
          ))}
        </div>

        <h2 className="text-sm font-black text-[#1e40af] uppercase tracking-widest mb-4 flex items-center">
          <span className="text-[#93c5fd] mr-2 text-lg">2.</span> Pareto by Subsystem
        </h2>
        <ParetoChart items={pareto.items} />

        <h2 className="text-sm font-black text-[#1e40af] uppercase tracking-widest mb-4 mt-10 flex items-center">
          <span className="text-[#93c5fd] mr-2 text-lg">3.</span> Performance Trend
        </h2>
        <TrendChart items={trend.items} granularity={trend.granularity} />

        <h2 className="text-sm font-black text-[#1e40af] uppercase tracking-widest mb-4 mt-10 flex items-center">
          <span className="text-[#93c5fd] mr-2 text-lg">4.</span> Top Failure Modes
        </h2>
        <div className="max-w-2xl"><DonutChart items={modes.items} /></div>

        <h2 className="text-sm font-black text-[#1e40af] uppercase tracking-widest mb-4 mt-10 flex items-center">
          <span className="text-[#93c5fd] mr-2 text-lg">5.</span> Key Findings
        </h2>
        <ul className="list-disc pl-6 space-y-2 text-sm text-slate-700 mb-8">
          {insights.map((i) => <li key={i.id ?? `${i.icon || "ins"}-${i.text}`}>{i.text}</li>)}
        </ul>

        <div className="border-t border-slate-200 pt-4 text-[10px] text-slate-400 uppercase tracking-widest">
          TRAMS RAMS Dashboard · Operations Control Center · Confidential Internal Use
        </div>
      </div>
    </div>
  );
}
